/**
 * React-Hülle um `@event-calendar/core`.
 *
 * **Warum diese Bibliothek.** Ein Dienstplan braucht die Ressourcen-Ansicht —
 * Zeilen sind Standort und Position, die Zeit läuft waagerecht. Bei
 * FullCalendar kostet genau die 480 $ je Entwickler und Jahr, bei Schedule-X
 * 479 € im Jahr; beider Kern ist zwar MIT, aber die eine Ansicht, um die es
 * hier geht, ist es nicht. `@event-calendar` stellt sie unter MIT bereit.
 *
 * **Warum eine eigene Hülle.** Die Bibliothek ist in Svelte geschrieben und
 * bringt keine React-Anbindung mit. Sie wird deshalb von Hand eingehängt. Das
 * ist überschaubar, hat aber zwei Fallstricke, die hier gelöst sind:
 *
 * 1. **Neu einhängen ist teuer und sichtbar.** Bei jeder Zustandsänderung eine
 *    neue Instanz zu bauen ließe den Kalender flackern und würfe die
 *    Scrollposition weg. Deshalb entsteht die Instanz genau einmal; alles
 *    Weitere geht über `setOption`.
 * 2. **Rückrufe altern.** Gäbe man `eventClick` einmal beim Erzeugen mit,
 *    schlösse es die damaligen Props ein und arbeitete für immer mit
 *    veraltetem Zustand. Deshalb liegen die Rückrufe in einem Ref, und die
 *    Bibliothek bekommt eine feste Weiche, die stets den aktuellen liest.
 */

import type { Calendar as EC } from '@event-calendar/core'
import { useEffect, useRef } from 'react'

import '@event-calendar/core/index.css'
import './calendar.css'

/** Ein Termin, wie ihn der Kalender darstellt. */
export type CalendarEvent = {
  id: string
  start: Date | string
  end: Date | string
  title: string
  /** Zeilenzuordnung in der Ressourcen-Ansicht. */
  resourceIds?: string[]
  backgroundColor?: string
  textColor?: string
  /** Nur lesen — verhindert Ziehen und Größenänderung für einzelne Termine. */
  editable?: boolean
  extendedProps?: Record<string, unknown>
}

export type CalendarResource = {
  id: string
  title: string
  eventBackgroundColor?: string
}

export type CalendarView =
  | 'dayGridMonth'
  | 'timeGridWeek'
  | 'timeGridDay'
  | 'listWeek'
  | 'resourceTimelineWeek'
  | 'resourceTimelineDay'

type Props = {
  view: CalendarView
  events: CalendarEvent[]
  resources?: CalendarResource[]
  /** Datum, dessen Zeitraum angezeigt wird. */
  date: Date
  /** Ziehen und Größe ändern erlauben. Nur für die Planung. */
  editable?: boolean
  height?: string
  /** Erster Stundenwert im Zeitraster — Gastronomie beginnt selten um 0 Uhr. */
  slotMinTime?: string
  slotMaxTime?: string
  /**
   * Breite einer Zeitspalte in der Timeline.
   *
   * ⚠️ Wichtig für die Wochen-Timeline: Mit Stundenspalten ist eine Woche
   * mehrere Bildschirme breit, und man sieht faktisch nur einen Tag. Gröbere
   * Spalten machen die Woche auf einen Blick lesbar.
   */
  slotDuration?: string
  onEventClick?: (eventId: string) => void
  /**
   * Verschoben: neue Zeiten und — beim Zug in eine andere Zeile — die neue
   * Ressource. `revert` macht es rückgängig, wenn der Server ablehnt.
   *
   * ⚠️ Die Ressource **muss** ausgewertet werden. Wer nur die Zeiten
   * übernimmt, speichert etwas anderes, als der Nutzer gerade gesehen hat:
   * Der Termin sitzt in der neuen Zeile, die Datenbank kennt die alte, und
   * beim nächsten Laden springt er kommentarlos zurück.
   */
  onEventDrop?: (
    eventId: string,
    start: Date,
    end: Date,
    revert: () => void,
    newResourceId?: string,
  ) => void
  onEventResize?: (eventId: string, start: Date, end: Date, revert: () => void) => void
  /** Klick auf eine leere Stelle — zum Anlegen. */
  onDateClick?: (date: Date, resourceId?: string) => void
  /**
   * Eigene Darstellung eines Termins als HTML.
   *
   * Die Vorgabe der Bibliothek ist eine einzige Textzeile. Für einen
   * Dienstplan ist das zu wenig: Uhrzeit, Wer und Was-fehlt müssen sich auf
   * einen Blick trennen lassen.
   */
  renderEvent?: (info: EC.EventContentInfo) => string
}

/** Schützt vor HTML in Namen — Titel kommen aus Nutzereingaben. */
export function escapeHtml(text: string): string {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
}

/**
 * Bibliothek erst laden, wenn ein Kalender wirklich gebraucht wird.
 *
 * ⚠️ Der Import muss **vollständig** dynamisch sein — auch `createCalendar`.
 * Ein einziger statischer Import oben in der Datei zieht das ganze Paket samt
 * Svelte-Laufzeit in das Hauptbündel, und die Aufteilung ist wirkungslos. Das
 * war hier zuerst der Fall: 193 kB gzip in einem Stück, für alle, die nie
 * einen Plan öffnen. Der Unterschied fällt auf einem Telefon im Gastraum auf,
 * nicht auf dem Entwicklungsrechner.
 *
 * Nur die Typen kommen statisch — die verschwinden beim Übersetzen.
 */
async function ladeBibliothek() {
  const mod = await import('@event-calendar/core')
  return {
    createCalendar: mod.createCalendar,
    destroyCalendar: mod.destroyCalendar,
    plugins: [mod.DayGrid, mod.TimeGrid, mod.List, mod.ResourceTimeline, mod.Interaction],
  }
}

export function Calendar(props: Props) {
  const container = useRef<HTMLDivElement>(null)
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const instance = useRef<any>(null)

  // Die Rückrufe liegen in einem Ref, nicht in den Optionen: Sonst hielte die
  // Bibliothek für immer die Fassung fest, die beim Erzeugen galt.
  const handlers = useRef(props)
  handlers.current = props

  useEffect(() => {
    let abgebrochen = false
    const ziel = container.current
    if (!ziel) return

    let aufraeumen: (() => void) | null = null

    ladeBibliothek().then(({ createCalendar, destroyCalendar, plugins }) => {
      if (abgebrochen || !ziel) return
      aufraeumen = () => {
        if (instance.current) {
          destroyCalendar(instance.current)
          instance.current = null
        }
      }

      instance.current = createCalendar(ziel, plugins, {
        view: handlers.current.view,
        date: handlers.current.date,
        events: handlers.current.events,
        resources: handlers.current.resources ?? [],
        locale: 'de',
        firstDay: 1,
        // Die Uhrzeit zweistellig und ohne AM/PM — deutsche Schreibweise.
        eventTimeFormat: { hour: '2-digit', minute: '2-digit', hour12: false },
        slotLabelFormat: { hour: '2-digit', minute: '2-digit', hour12: false },
        dayHeaderFormat: { weekday: 'short', day: '2-digit', month: '2-digit' },
        nowIndicator: true,
        height: handlers.current.height ?? '640px',
        slotMinTime: handlers.current.slotMinTime ?? '06:00',
        slotMaxTime: handlers.current.slotMaxTime ?? '30:00',
        ...(handlers.current.slotDuration
          ? { slotDuration: handlers.current.slotDuration }
          : {}),
        // Die eigene Kopfleiste ist besser in die Oberfläche eingepasst als
        // die mitgelieferte.
        headerToolbar: { start: '', center: '', end: '' },
        editable: handlers.current.editable ?? false,
        eventStartEditable: handlers.current.editable ?? false,
        eventDurationEditable: handlers.current.editable ?? false,
        noEventsContent: 'Nichts eingeplant.',
        buttonText: { today: 'Heute' },
        // `locale: 'de'` übersetzt Wochentage und Monate, aber nicht diese
        // beiden Beschriftungen — die kommen aus eigenen Optionen.
        allDayContent: 'ganztägig',
        // Zeilenköpfe zweizeilig: Position kräftig, Standort zurückgenommen.
        // Der Trenner „ · " kommt aus `shiftsToResources`; steht keiner drin,
        // bleibt es bei einer Zeile.
        resourceLabelContent: (info: EC.ResourceLabelInfo) => {
          const titel = String(info.resource.title ?? '')
          const [position, ...rest] = titel.split(' · ')
          const ort = rest.join(' · ')
          return {
            html: ort
              ? `<span class="dp-res"><span class="dp-res__pos">${escapeHtml(
                  position ?? '',
                )}</span><span class="dp-res__loc">${escapeHtml(ort)}</span></span>`
              : escapeHtml(titel),
          }
        },
        ...(handlers.current.renderEvent
          ? {
              eventContent: (info: EC.EventContentInfo) => ({
                html: handlers.current.renderEvent!(info),
              }),
            }
          : {}),
        moreLinkContent: (info: EC.MoreLinkInfo) => `+ ${info.num} weitere`,
        // Die Typen der Bibliothek statt eigener: Sie erlaubt Zahlen als ID,
        // unsere IDs sind UUID-Zeichenketten — `String()` überbrückt das,
        // ohne die Signatur zu verbiegen.
        eventClick: (info: EC.EventClickInfo) =>
          handlers.current.onEventClick?.(String(info.event.id)),
        eventDrop: (info: EC.EventDropInfo) =>
          handlers.current.onEventDrop?.(
            String(info.event.id),
            info.event.start,
            info.event.end,
            info.revert,
            info.newResource?.id?.toString(),
          ),
        eventResize: (info: EC.EventResizeInfo) =>
          handlers.current.onEventResize?.(
            String(info.event.id),
            info.event.start,
            info.event.end,
            info.revert,
          ),
        dateClick: (info: EC.DateClickInfo) =>
          handlers.current.onDateClick?.(info.date, info.resource?.id?.toString()),
      })
    })

    return () => {
      // `abgebrochen` fängt den Fall ab, dass die Komponente verschwindet,
      // bevor die Bibliothek geladen ist — sonst hinge eine Instanz an einem
      // Element, das es nicht mehr gibt.
      abgebrochen = true
      aufraeumen?.()
    }
    // Absichtlich leer: Die Instanz entsteht einmal, alles Weitere läuft über
    // `setOption` in den Effekten darunter. Ein Neuaufbau bei jeder Änderung
    // würde flackern und die Scrollposition verlieren. Die Props liegen im
    // Ref, deshalb beschwert sich die Abhängigkeitsprüfung zu Recht nicht.
  }, [])

  // Ansicht, Datum, Termine und Ressourcen einzeln nachziehen.
  useEffect(() => {
    instance.current?.setOption('view', props.view)
  }, [props.view])

  useEffect(() => {
    instance.current?.setOption('date', props.date)
  }, [props.date])

  useEffect(() => {
    instance.current?.setOption('events', props.events)
  }, [props.events])

  useEffect(() => {
    instance.current?.setOption('resources', props.resources ?? [])
  }, [props.resources])

  // ⚠️ Auch Höhe und Spaltenbreite müssen nachgezogen werden. Sie nur beim
  // Erzeugen zu setzen hieße: Die Werte der *ersten* Ansicht gelten für alle
  // folgenden — die Wochenansicht erbte dann das `auto` der Timeline und wurde
  // 1244 Pixel hoch, statt in ihrem Kasten zu scrollen.
  useEffect(() => {
    instance.current?.setOption('height', props.height ?? '640px')
  }, [props.height])

  useEffect(() => {
    instance.current?.setOption('slotDuration', props.slotDuration ?? '00:30')
  }, [props.slotDuration])

  useEffect(() => {
    const editable = props.editable ?? false
    instance.current?.setOption('editable', editable)
    instance.current?.setOption('eventStartEditable', editable)
    instance.current?.setOption('eventDurationEditable', editable)
  }, [props.editable])

  return <div ref={container} className="dp-calendar" />
}
